import { useTheme } from '@mui/material';
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined';
import DSDialog from '../../../design-system/DSDialog';
import DSBox from '../../../design-system/DSBox';
import { DSTypography } from '../../../design-system/Typography';
import { ArtistProfileTemplate, SectionTemplateKey } from '../../../api/public';
import { ReactComponent as PlayIcon } from '../../../design-system/Icons/Play.svg';

type AddSectionDialogProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (sectionType: string) => void;
  templates: ArtistProfileTemplate[];
};

const ImageIcon = ({
  width = 100,
  height = 100,
}: {
  width?: number;
  height?: number;
}) => (
  <DSBox
    sx={{
      width,
      height,
      borderRadius: '4px',
      border: '2px solid',
      borderColor: 'action.selected',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <PhotoOutlinedIcon
      sx={{ fontSize: Math.min(width, height) * 0.3, color: 'action.selected' }}
    />
  </DSBox>
);

const TextLines = ({ lineWidth = 90 }: { lineWidth?: number }) => (
  <DSBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
    <DSBox
      sx={{
        width: lineWidth,
        height: 3,
        borderRadius: 0.5,
        backgroundColor: 'white.20',
      }}
    />
    <DSBox
      sx={{
        width: lineWidth * 0.7,
        height: 3,
        borderRadius: 0.5,
        backgroundColor: 'white.10',
      }}
    />
    <DSBox
      sx={{
        width: lineWidth * 0.85,
        height: 3,
        borderRadius: 0.5,
        backgroundColor: 'white.10',
      }}
    />
    <DSBox
      sx={{
        width: lineWidth * 0.6,
        height: 3,
        borderRadius: 0.5,
        backgroundColor: 'white.10',
      }}
    />
  </DSBox>
);

type ThumbnailRendererProps = {
  sectionKey: string;
};

const ThumbnailRenderer = ({ sectionKey }: ThumbnailRendererProps) => {
  switch (sectionKey) {
    case SectionTemplateKey.FULL_WIDTH_VIDEO:
      return (
        <DSBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid',
            borderColor: 'action.selected',
            width: '90%',
            height: '90%',
            borderRadius: '4px',
          }}
        >
          <PlayIcon width={48} height={48} />
        </DSBox>
      );

    case SectionTemplateKey.VIDEO_TEXT:
      return (
        <DSBox sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DSBox
            sx={{
              width: 100,
              height: 110,
              border: '2px solid',
              borderColor: 'action.selected',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <PlayIcon width={36} height={36} />
          </DSBox>
          <TextLines lineWidth={80} />
        </DSBox>
      );

    case SectionTemplateKey.IMAGE_TEXT:
      return (
        <DSBox sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ImageIcon width={100} height={110} />
          <TextLines lineWidth={80} />
        </DSBox>
      );

    case SectionTemplateKey.DOUBLE_IMAGE:
      return (
        <DSBox sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ImageIcon width={100} height={110} />
          <ImageIcon width={100} height={110} />
        </DSBox>
      );

    case SectionTemplateKey.FULL_WIDTH_IMAGE:
      return <ImageIcon width={200} height={110} />;

    default:
      return null;
  }
};

const ALLOWED_KEYS = Object.values(SectionTemplateKey) as string[];

const AddSectionDialog = ({
  open,
  onClose,
  onSelect,
  templates,
}: AddSectionDialogProps) => {
  const { palette } = useTheme();

  const sectionList = templates
    .filter((template) => ALLOWED_KEYS.includes(template.key))
    .map((template) => ({
      key: template.key,
      title: template.name,
      description: template.description,
    }));

  return (
    <DSDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '.MuiDialog-paper': {
          overflow: 'auto',
          maxHeight: '80vh',
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: palette.background.default,
          borderRadius: '24px',
          p: { xxs: '10px', sm: '32px' },
          gap: '20px',
        },
      }}
    >
      <DSBox>
        <DSTypography variant="h5">Add Section</DSTypography>
        <DSTypography variant="body2" color="text.secondary">
          Choose a section to enrich your artist page.
        </DSTypography>
      </DSBox>

      <DSBox
        sx={{
          display: 'grid',
          gridTemplateColumns: { xxs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: '16px',
        }}
      >
        {sectionList.map((section) => (
          <DSBox
            key={section.key}
            onClick={() => {
              onSelect(section.key);
              onClose();
            }}
            sx={{
              cursor: 'pointer',
              borderRadius: '16px',
              backgroundColor: 'paper.elevation2',
              overflow: 'hidden',
              boxShadow: 4,
              border: '3px solid transparent',
              transition: 'border-color 0.2s',
              '&:hover': {
                borderColor: 'action.active',
              },
            }}
          >
            <DSBox
              sx={{
                height: 151,
                backgroundColor: 'secondary.dark',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ThumbnailRenderer sectionKey={section.key} />
            </DSBox>
            <DSBox sx={{ pt: '16px', px: '16px', pb: '16px' }}>
              <DSTypography
                sx={{
                  fontFamily: 'Gilroy-SemiBold',
                  fontSize: '16px',
                  fontWeight: 600,
                  lineHeight: 1.2,
                  letterSpacing: '0.15px',
                  color: 'common.white',
                }}
              >
                {section.title}
              </DSTypography>
              <DSTypography
                sx={{
                  fontFamily: 'Gilroy-Medium',
                  fontSize: '13px',
                  lineHeight: 1.43,
                  letterSpacing: '0.17px',
                  color: 'text.secondary',
                  mt: '4px',
                }}
              >
                {section.description}
              </DSTypography>
            </DSBox>
          </DSBox>
        ))}
      </DSBox>
    </DSDialog>
  );
};

export default AddSectionDialog;
